import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Select,
  Spinner,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  HStack, VStack, Button, Badge, useToast
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

function getRecentMonths(n = 24) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    months.push(`${year}${month}`);
  }
  return months;
}

export default function AdminSettlementRequest() {
  const [mainTab, setMainTab] = useState("request"); // "request" or "summary"
  const [settleTab, setSettleTab] = useState("user");
  const [settleMonth, setSettleMonth] = useState(() => getRecentMonths(1)[0]);
  const [settleStatus, setSettleStatus] = useState("");
  const [userSettle, setUserSettle] = useState([]);
  const [copyrightSettle, setCopyrightSettle] = useState([]);
  const [loadingSettle, setLoadingSettle] = useState(false);
  const toast = useToast();
  const [userEmail, setUserEmail] = useState("");
  const [userEmailInput, setUserEmailInput] = useState("");
  const [copyrightEmail, setCopyrightEmail] = useState("");
  const [copyrightEmailInput, setCopyrightEmailInput] = useState("");
  const [userTotalData, setUserTotalData] = useState([]);
  const [userTotalLoading, setUserTotalLoading] = useState(false);
  const [copyrightTotalData, setCopyrightTotalData] = useState([]);
  const [copyrightTotalLoading, setCopyrightTotalLoading] = useState(false);

  // 정산 합계 탭 내부 전환 상태
  const [summaryTab, setSummaryTab] = useState("user"); // "user" or "copyright"
  const [summaryEmail, setSummaryEmail] = useState("");
  const [summaryEmailInput, setSummaryEmailInput] = useState("");
  const [summaryStatus, setSummaryStatus] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Pagination state for settlement tabs
  const [userSettlePage, setUserSettlePage] = useState(1);
  const [userSettleTotal, setUserSettleTotal] = useState(0);
  const [copyrightSettlePage, setCopyrightSettlePage] = useState(1);
  const [copyrightSettleTotal, setCopyrightSettleTotal] = useState(0);
  const PAGE_SIZE = 10;

  // 정산 신청 데이터 fetch (user)
  useEffect(() => {
    async function fetchSettleData() {
      setLoadingSettle(true);
      if (settleTab === "user") {
        // Use Supabase RPC
        let { data, error } = await supabase.rpc("get_payout_withdraw_by_email_status", {
          p_email: userEmail.trim() === "" ? null : userEmail.trim(),
          p_status: settleStatus.trim() === "" ? null : settleStatus.trim(),
          p_page: userSettlePage,
        });
        if (error) {
          toast({ title: "데이터 불러오기 실패", status: "error" });
          setUserSettle([]);
          setUserSettleTotal(0);
        } else {
          setUserSettle(data || []);
          // 총 개수는 알 수 없으므로, 다음 페이지가 비어있으면 마지막 페이지로 간주
          setUserSettleTotal((data && data.length === PAGE_SIZE) ? (userSettlePage + 1) * PAGE_SIZE : (userSettlePage - 1) * PAGE_SIZE + (data ? data.length : 0));
        }
      } else {
        // Use Supabase RPC for copyright
        let { data, error } = await supabase.rpc("get_payout_withdraw_copyrights_by_email_status", {
          p_email: copyrightEmail.trim() === "" ? null : copyrightEmail.trim(),
          p_status: settleStatus.trim() === "" ? null : settleStatus.trim(),
          p_page: copyrightSettlePage,
        });
        if (error) {
          toast({ title: "데이터 불러오기 실패", status: "error" });
          setCopyrightSettle([]);
          setCopyrightSettleTotal(0);
        } else {
          setCopyrightSettle(data || []);
          setCopyrightSettleTotal((data && data.length === PAGE_SIZE) ? (copyrightSettlePage + 1) * PAGE_SIZE : (copyrightSettlePage - 1) * PAGE_SIZE + (data ? data.length : 0));
        }
      }
      setLoadingSettle(false);
    }
    fetchSettleData();
  }, [settleTab, settleStatus, userEmail, userSettlePage, copyrightEmail, copyrightSettlePage]);

  // Fetch total data for user tab
  useEffect(() => {
    async function fetchTotal() {
      if (settleTab !== "user" || !userEmail.trim()) {
        setUserTotalData([]);
        return;
      }
      setUserTotalLoading(true);
      const { data, error } = await supabase.rpc("get_payout_withdraw_by_email_status_total", {
        p_email: userEmail.trim(),
        p_status: settleStatus.trim() === "" ? null : settleStatus.trim(),
      });
      if (error) {
        setUserTotalData([]);
      } else {
        setUserTotalData(data || []);
      }
      setUserTotalLoading(false);
    }
    fetchTotal();
  }, [settleTab, userEmail, settleStatus]);

  // Fetch total data for copyright tab
  useEffect(() => {
    async function fetchTotal() {
      if (settleTab !== "copyright" || !copyrightEmail.trim()) {
        setCopyrightTotalData([]);
        return;
      }
      setCopyrightTotalLoading(true);
      const { data, error } = await supabase.rpc("get_payout_withdraw_copyrights_by_email_status_total", {
        p_email: copyrightEmail.trim(),
        p_status: settleStatus.trim() === "" ? null : settleStatus.trim(),
      });
      if (error) {
        setCopyrightTotalData([]);
      } else {
        setCopyrightTotalData(data || []);
      }
      setCopyrightTotalLoading(false);
    }
    fetchTotal();
  }, [settleTab, copyrightEmail, settleStatus]);

  // 정산 합계 데이터 fetch (사용자/권리자 분기)
  useEffect(() => {
    if (mainTab !== "summary") {
      setSummaryData([]);
      return;
    }
    fetchSummaryData();
  }, [mainTab, summaryTab, summaryEmail, summaryStatus]);

  // summaryTab(사용자/영상 권리자) 변경 시에도 fetch
  useEffect(() => {
    if (mainTab === 'summary') {
      fetchSummaryData();
    }
    // eslint-disable-next-line
  }, [summaryTab]);

  // 상태 변경 핸들러
  const handleStatusChange = async (id, table) => {
    const { error } = await supabase
      .from(table)
      .update({ status: "[Admin] Completed" })
      .eq("id", id);
    if (!error) {
      toast({ title: "정산 완료 처리됨", status: "success" });
      // 데이터 새로고침
      if (table === "payout_withdraw") setUserSettle(prev => prev.map(item => item.id === id ? { ...item, status: "[Admin] Completed" } : item));
      if (table === "payout_withdraw_copyrights") setCopyrightSettle(prev => prev.map(item => item.id === id ? { ...item, status: "[Admin] Completed" } : item));
    } else {
      toast({ title: "상태 변경 실패", status: "error" });
    }
  };

  // 미정산 처리 핸들러
  const handleNotCompleteStatus = async (id, table) => {
    const { error } = await supabase
      .from(table)
      .update({ status: "[Admin] Not Complete" })
      .eq("id", id);
    if (!error) {
      toast({ title: "미정산 처리됨", status: "success" });
      // 데이터 새로고침
      if (table === "payout_withdraw") setUserSettle(prev => prev.map(item => item.id === id ? { ...item, status: "[Admin] Not Complete" } : item));
      if (table === "payout_withdraw_copyrights") setCopyrightSettle(prev => prev.map(item => item.id === id ? { ...item, status: "[Admin] Not Complete" } : item));
    } else {
      toast({ title: "상태 변경 실패", status: "error" });
    }
  };

  // 필터링 (user tab은 서버에서 처리하므로 그대로)
  const filteredUserSettle = userSettle;
  const filteredCopyrightSettle = copyrightSettle.filter(item => !settleStatus || item.status === settleStatus);
  const userSettleTotalPages = Math.max(1, Math.ceil(userSettleTotal / PAGE_SIZE));
  const copyrightSettleTotalPages = Math.max(1, Math.ceil(copyrightSettleTotal / PAGE_SIZE));

  // 계산 함수
  function calcTotalSummary(data) {
    let completed = 0, requested = 0, total = 0;
    let personalRequested = 0, corpRequested = 0;
    let personalCount = 0, corpCount = 0;
    let payoutTypePersonal = "개인";
    let payoutTypeCorp = "일반/법인 사업자";
    data.forEach(row => {
      if (row.status === "[Admin] Completed") completed += Number(row.payout_amount || 0);
      if (row.status === "[User] Request") {
        requested += Number(row.payout_amount || 0);
        if (row.payout_type === payoutTypePersonal) {
          personalRequested += Number(row.payout_amount || 0);
          personalCount++;
        }
        if (row.payout_type === payoutTypeCorp) {
          corpRequested += Number(row.payout_amount || 0);
          corpCount++;
        }
      }
    });
    total = completed + requested;
    // 개인: 국세 0.3%, 지방세 0.03%, 최종정산금액 = 미정산수익 - 0.33%
    // 법인: 부가세 10%, 최종정산금액 = 미정산수익 + 10%
    let tax = 0, localTax = 0, vat = 0, finalAmount = 0;
    if (personalRequested > 0) {
      tax = Math.floor(personalRequested * 0.003);
      localTax = Math.floor(personalRequested * 0.0003);
      finalAmount += Math.floor(personalRequested * (1 - 0.0033));
    }
    if (corpRequested > 0) {
      vat = Math.floor(corpRequested * 0.1);
      finalAmount += Math.floor(corpRequested * 1.1);
    }
    return {
      completed,
      requested,
      total,
      tax,
      localTax,
      vat,
      finalAmount,
      personalRequested,
      corpRequested,
    };
  }
  const totalSummary = calcTotalSummary(userTotalData);

  // 계산 함수 (복제)
  function calcTotalSummary(data) {
    let completed = 0, requested = 0, total = 0;
    let personalRequested = 0, corpRequested = 0;
    let payoutTypePersonal = "개인";
    let payoutTypeCorp = "일반/법인 사업자";
    data.forEach(row => {
      if (row.status === "[Admin] Completed") completed += Number(row.payout_amount || 0);
      if (row.status === "[User] Request") {
        requested += Number(row.payout_amount || 0);
        if (row.payout_type === payoutTypePersonal) {
          personalRequested += Number(row.payout_amount || 0);
        }
        if (row.payout_type === payoutTypeCorp) {
          corpRequested += Number(row.payout_amount || 0);
        }
      }
    });
    total = completed + requested;
    let tax = 0, localTax = 0, vat = 0, finalAmount = 0;
    if (personalRequested > 0) {
      tax = Math.floor(personalRequested * 0.003);
      localTax = Math.floor(personalRequested * 0.0003);
      finalAmount += Math.floor(personalRequested * (1 - 0.0033));
    }
    if (corpRequested > 0) {
      vat = Math.floor(corpRequested * 0.1);
      finalAmount += Math.floor(corpRequested * 1.1);
    }
    return {
      completed,
      requested,
      total,
      tax,
      localTax,
      vat,
      finalAmount,
      personalRequested,
      corpRequested,
    };
  }
  const copyrightTotalSummary = calcTotalSummary(copyrightTotalData);

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    if (!userTotalData || userTotalData.length === 0) {
      toast({ title: "다운로드할 데이터가 없습니다.", status: "warning" });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("정산신청내역");
    const headers = [
      "회원", "정산 계좌", "정산 월", "정산 타입", "총 수익", "국세", "지방세", "정산금", "음원수익", "영상수익(총)", "영상수익(크리에이터)", "영상수익(권리자+레이블리)", "쇼핑수익(총)", "쇼핑수익(크리에이터)", "쇼핑수익(권리자+레이블리)", "상태", "신청일"
    ];
    worksheet.addRow(headers);
    userTotalData.forEach(row => {
      worksheet.addRow([
        row.user,
        `${row.bank || ''} ${row.bank_account_number || ''} ${row.bank_account_holder || ''}`,
        row.payout_month,
        row.payout_type,
        row.payout_amount,
        row.tax_gst,
        row.tax_pst,
        row.final_amount,
        row.music_revenue,
        row.total_video_revenue,
        row.video_creator_revenue,
        row.video_copyright_laeebly_revenue,
        row.total_shopping_revenue,
        row.shopping_creator_revenue,
        row.shopping_copyright_laeebly_revenue,
        row.status,
        row.created_date,
      ]);
    });
    worksheet.columns.forEach(col => { col.width = 18; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `정산신청내역_${userEmail}.xlsx`);
    toast({ title: "엑셀 다운로드 완료", status: "success" });
  };

  // 엑셀 다운로드 (복제)
  const handleCopyrightExcelDownload = async () => {
    if (!copyrightTotalData || copyrightTotalData.length === 0) {
      toast({ title: "다운로드할 데이터가 없습니다.", status: "warning" });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("정산신청내역_권리자");
    const headers = [
      "회원", "정산 계좌", "정산 월", "정산 타입", "총 수익", "국세", "지방세", "정산금", "음원수익", "영상수익(총)", "영상수익(크리에이터)", "영상수익(권리자+레이블리)", "쇼핑수익(총)", "쇼핑수익(크리에이터)", "쇼핑수익(권리자+레이블리)", "상태", "신청일"
    ];
    worksheet.addRow(headers);
    copyrightTotalData.forEach(row => {
      worksheet.addRow([
        row.user,
        `${row.bank || ''} ${row.bank_account_number || ''} ${row.bank_account_holder || ''}`,
        row.payout_month,
        row.payout_type,
        row.payout_amount,
        row.tax_gst,
        row.tax_pst,
        row.final_amount,
        row.music_revenue,
        row.total_video_revenue,
        row.video_creator_revenue,
        row.video_copyright_laeebly_revenue,
        row.total_shopping_revenue,
        row.shopping_creator_revenue,
        row.shopping_copyright_laeebly_revenue,
        row.status,
        row.created_date,
      ]);
    });
    worksheet.columns.forEach(col => { col.width = 18; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `정산신청내역_권리자_${copyrightEmail}.xlsx`);
    toast({ title: "엑셀 다운로드 완료", status: "success" });
  };

  // 정산 합계 엑셀 다운로드
  const handleSummaryExcelDownload = async () => {
    if (!summaryData || summaryData.length === 0) {
      toast({ title: "다운로드할 데이터가 없습니다.", status: "warning" });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("정산합계내역");
    const headers = [
      "회원", "정산 계좌", "상태", "총 정산금", "총 수익", "총 음원수익", "총 영상수익", "총 쇼핑수익"
    ];
    worksheet.addRow(headers);
    summaryData.forEach(row => {
      worksheet.addRow([
        row.user,
        `${row.bank || ''} ${row.bank_account_number || ''} ${row.bank_account_holder || ''}`,
        row.status === "[Admin] Completed" ? "정산 완료" : 
        row.status === "[User] Request" ? "정산 대기" : row.status,
        row.total_final_amount,
        row.total_payout_amount,
        row.total_music_revenue,
        row.total_video_revenue,
        row.total_shopping_revenue,
      ]);
    });
    worksheet.columns.forEach(col => { col.width = 18; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `정산합계내역_${summaryEmail || '전체'}.xlsx`);
    toast({ title: "엑셀 다운로드 완료", status: "success" });
  };

  // summaryData 즉시 새로고침을 위한 fetch 함수 분리
  const fetchSummaryData = async () => {
    setSummaryLoading(true);
    let data, error;
    if (summaryTab === "user") {
      ({ data, error } = await supabase.rpc("get_payout_withdraw_sum_total_by_user", {
        p_email: summaryEmail.trim() === "" ? null : summaryEmail.trim(),
        p_status: summaryStatus.trim() === "" ? null : summaryStatus.trim(),
      }));
    } else {
      ({ data, error } = await supabase.rpc("get_payout_withdraw_copyrights_sum_total_by_user", {
        p_email: summaryEmail.trim() === "" ? null : summaryEmail.trim(),
        p_status: summaryStatus.trim() === "" ? null : summaryStatus.trim(),
      }));
    }
    if (error) {
      toast({ title: "정산 합계 데이터 불러오기 실패", status: "error" });
      setSummaryData([]);
    } else {
      setSummaryData(data || []);
    }
    setSummaryLoading(false);
  };

  const handleSummaryCompleteAll = async (user) => {
    let table = summaryTab === 'user' ? 'payout_withdraw' : 'payout_withdraw_copyrights';
    const { error } = await supabase
      .from(table)
      .update({ status: '[Admin] Completed' })
      .eq('user', user)
      .eq('status', '[User] Request');
    if (!error) {
      toast({ title: '전체 정산 완료 처리됨', status: 'success' });
      // 즉시 목록 새로고침
      fetchSummaryData();
    } else {
      toast({ title: '상태 변경 실패', status: 'error' });
    }
  };

  // 테이블 컬럼 렌더링
  const renderSettleRow = (item, type) => (
    <SimpleGrid columns={17} spacing={2} py={2} borderBottom="1px solid #f8f8f8" fontSize="sm" alignItems="center" key={item.id}>
      <Text>{item.user}</Text>
      <Text>{item.bank} {item.bank_account_number}<br/>{item.bank_account_holder}</Text>
      <Text>{item.payout_month || "-"}</Text>
      <Text>{item.payout_type || "-"}</Text>
      <Text>₩ {Number(item.payout_amount || 0).toLocaleString()}</Text>
      <Text color="red.500">
        ₩ {(() => {
          if (item.payout_type === "일반/법인 사업자") {
            return Number(item.vat || 0);
          } else {
            return Number(item.tax_gst || 0);
          }
        })().toLocaleString()}
      </Text>
      <Text color="red.500">
        ₩ {(() => {
          if (item.payout_type === "일반/법인 사업자") {
            return 0;
          } else {
            return Number(item.tax_pst || 0);
          }
        })().toLocaleString()}
      </Text>
      <Text>₩ {Number(item.final_amount || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.music_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_video_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.video_creator_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.video_copyright_laeebly_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_shopping_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.shopping_creator_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.shopping_copyright_laeebly_revenue || 0).toLocaleString()}</Text>
      <Badge colorScheme={
        item.status === "[Admin] Completed" ? "green" : 
        item.status === "[Admin] Not Complete" ? "red" : "yellow"
      }>
        {item.status === "[Admin] Completed" ? "정산 완료" : 
         item.status === "[Admin] Not Complete" ? "정산 완료 but 못받음" : "정산 대기"}
      </Badge>
      <VStack spacing={1} align="start">
        {(item.status === "[User] Request" || item.status === "[Admin] Not Complete") && (
          <>
            <Button size="xs" colorScheme="teal" onClick={() => handleStatusChange(item.id, type === "user" ? "payout_withdraw" : "payout_withdraw_copyrights")}>정산 완료 처리</Button>
            {/* {item.status === "[User] Request" && (
              <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleNotCompleteStatus(item.id, type === "user" ? "payout_withdraw" : "payout_withdraw_copyrights")}>미정산받은 금액으로 처리</Button>
            )} */}
          </>
        )}
      </VStack>
    </SimpleGrid>
  );

  // 정산 합계 테이블 컬럼 렌더링
  const renderSummaryRow = (item) => (
    <SimpleGrid columns={8} spacing={2} py={2} borderBottom="1px solid #f8f8f8" fontSize="sm" alignItems="center" key={`${item.user}-${item.status}`}>
      <Text>{item.user}</Text>
      <Text>{item.bank} {item.bank_account_number}<br/>{item.bank_account_holder}</Text>
      <Badge colorScheme={
        item.status === "[Admin] Completed" ? "green" : 
        item.status === "[User] Request" ? "yellow" : "gray"
      }>
        {item.status === "[Admin] Completed" ? "정산 완료" : 
         item.status === "[User] Request" ? "정산 대기" : item.status}
      </Badge>
      <Text fontWeight="bold">₩ {Number(item.total_final_amount || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_payout_amount || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_music_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_video_revenue || 0).toLocaleString()}</Text>
      <Text>₩ {Number(item.total_shopping_revenue || 0).toLocaleString()}</Text>
    </SimpleGrid>
  );

  return (
    <Box minH="100vh" bg="#f7fafd" p={6}>
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        {/* 상단 메인 탭 버튼 - 가로 100% */}
        <Flex gap={0} w="100%" borderBottom="1px solid #e2e8f0" mb={10}>
          <Button
            w="50%"
            h="60px"
            fontWeight="bold"
            fontSize="xl"
            borderRadius="2xl 0 0 0"
            borderRightRadius={0}
            border="none"
            borderRight="1px solid #e2e8f0"
            boxShadow="none"
            bg={mainTab === "request" ? "rgba(100,100,100,0.08)" : "transparent"}
            color={mainTab === "request" ? "teal.700" : "gray.500"}
            onClick={() => setMainTab("request")}
            transition="all 0.2s"
          >
            정산 신청
          </Button>
          <Button
            w="50%"
            h="60px"
            fontWeight="bold"
            fontSize="xl"
            borderRadius="0 2xl 0 0"
            borderLeftRadius={0}
            border="none"
            boxShadow="none"
            bg={mainTab === "summary" ? "rgba(100,100,100,0.08)" : "transparent"}
            color={mainTab === "summary" ? "teal.700" : "gray.500"}
            onClick={() => setMainTab("summary")}
            transition="all 0.2s"
          >
            정산 합계
          </Button>
        </Flex>
        {/* 정산 신청 영역 */}
        {mainTab === "request" && (
          <Box>
            <Flex align="center" borderBottom="1px solid #e2e8f0" mb={0}>
              {/* 사용자/영상 권리자 버튼 탭 */}
              <HStack spacing={0}>
                <Button
                  borderRadius="2xl 0 0 2xl"
                  borderRightRadius={0}
                  fontWeight="bold"
                  fontSize="md"
                  px={8}
                  py={4}
                  bg={settleTab === "user" ? "rgba(100,100,100,0.08)" : "transparent"}
                  color={settleTab === "user" ? "teal.700" : "gray.500"}
                  border="1px solid #e2e8f0"
                  borderRight="none"
                  onClick={() => setSettleTab("user")}
                >
                  사용자
                </Button>
                <Button
                  borderRadius="0 2xl 2xl 0"
                  borderLeftRadius={0}
                  fontWeight="bold"
                  fontSize="md"
                  px={8}
                  py={4}
                  bg={settleTab === "copyright" ? "rgba(100,100,100,0.08)" : "transparent"}
                  color={settleTab === "copyright" ? "teal.700" : "gray.500"}
                  border="1px solid #e2e8f0"
                  borderLeft="none"
                  onClick={() => setSettleTab("copyright")}
                >
                  영상 권리자
                </Button>
              </HStack>
              {/* 검색 섹션 */}
              {settleTab === "user" && (
                <Flex ml="auto" align="center" gap={4} px={4} py={2}>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>이메일</Text>
                    <HStack>
                      <input
                        type="text"
                        value={userEmailInput}
                        onChange={e => setUserEmailInput(e.target.value)}
                        placeholder="사용자 이메일 검색"
                        style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', minWidth: 180, fontSize: '14px' }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setUserEmail(userEmailInput);
                            setUserSettlePage(1);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={() => { setUserEmail(userEmailInput); setUserSettlePage(1); }}
                      >
                        검색
                      </Button>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>상태</Text>
                    <Select value={settleStatus} onChange={e => { setSettleStatus(e.target.value); setUserSettlePage(1); }} w="120px">
                      <option value="">전체</option>
                      <option value="[User] Request">정산 대기</option>
                      <option value="[Admin] Completed">정산 완료</option>
                    </Select>
                  </Box>
                </Flex>
              )}
              {settleTab === "copyright" && (
                <Flex ml="auto" align="center" gap={4} px={4} py={2}>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>이메일</Text>
                    <HStack>
                      <input
                        type="text"
                        value={copyrightEmailInput}
                        onChange={e => setCopyrightEmailInput(e.target.value)}
                        placeholder="권리자 이메일 검색"
                        style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', minWidth: 180, fontSize: '14px' }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setCopyrightEmail(copyrightEmailInput);
                            setCopyrightSettlePage(1);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={() => { setCopyrightEmail(copyrightEmailInput); setCopyrightSettlePage(1); }}
                      >
                        검색
                      </Button>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>상태</Text>
                    <Select value={settleStatus} onChange={e => { setSettleStatus(e.target.value); setCopyrightSettlePage(1); }} w="120px">
                      <option value="">전체</option>
                      <option value="[User] Request">정산 대기</option>
                      <option value="[Admin] Completed">정산 완료</option>
                    </Select>
                  </Box>
                </Flex>
              )}
            </Flex>
            {/* 사용자 영역 */}
            {settleTab === "user" && (
              <>
                {/* 토탈 섹션 - 사용자 */}
                {userEmail && (
                  <Box bg="#f8fafd" borderRadius="xl" p={4} mb={4} boxShadow="sm">
                    {userTotalLoading ? (
                      <Flex align="center" justify="center" minH="60px"><Spinner size="sm" /></Flex>
                    ) : (
                      <Flex align="center" justify="space-between" gap={4} wrap="wrap">
                        <HStack spacing={8} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="bold" color="blue.700">총 수익: <span style={{color:'#222'}}>{totalSummary.total.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="teal.700">정산 완료 수익: <span style={{color:'#222'}}>{totalSummary.completed.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="orange.600">미정산 수익: <span style={{color:'#222'}}>{totalSummary.requested.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">국세: <span style={{color:'#222'}}>{totalSummary.tax.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">지방세: <span style={{color:'#222'}}>{totalSummary.localTax.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="purple.600">부가세: <span style={{color:'#222'}}>{totalSummary.vat.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="green.700">최종 정산 금액: <span style={{color:'#222'}}>{totalSummary.finalAmount.toLocaleString()}원</span></Text>
                        </HStack>
                        <Button colorScheme="green" size="sm" onClick={handleExcelDownload}>
                          엑셀 다운로드
                        </Button>
                      </Flex>
                    )}
                  </Box>
                )}
                <SimpleGrid columns={17} spacing={2} fontWeight="bold" borderBottom="none" pb={2} fontSize="xs">
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">회원</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산 계좌</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산 월</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산 타입</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">총 수익</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">국세</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">지방세</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산금</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">음원수익</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">영상수익(총)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">영상수익(크리에이터)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">영상수익(권리자+레이블리)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">쇼핑수익(총)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">쇼핑수익(크리에이터)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">쇼핑수익(권리자+레이블리)</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">상태</Text>
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">처리</Text>
                </SimpleGrid>
                {loadingSettle ? (
                  <Flex justify="center" py={4}><Spinner /></Flex>
                ) : filteredUserSettle.length > 0 ? (
                  <>
                    {filteredUserSettle.map(item => renderSettleRow(item, "user"))}
                    <Flex justify="center" align="center" mt={4} gap={2}>
                      <Button size="sm" onClick={() => setUserSettlePage(p => Math.max(1, p - 1))} isDisabled={userSettlePage === 1}>이전</Button>
                      <Text fontWeight="bold" fontSize="sm">{userSettlePage} / {userSettleTotalPages}</Text>
                      <Button size="sm" onClick={() => setUserSettlePage(p => Math.min(userSettleTotalPages, p + 1))} isDisabled={userSettlePage === userSettleTotalPages}>다음</Button>
                    </Flex>
                  </>
                ) : (
                  <Text textAlign="center" py={4} color="gray.500">정산 신청 내역이 없습니다.</Text>
                )}
              </>
            )}
            {/* 권리자 영역 */}
            {settleTab === "copyright" && (
              <>
                {/* 토탈 섹션 - 권리자 */}
                {copyrightEmail && (
                  <Box bg="#f8fafd" borderRadius="xl" p={4} mb={4} boxShadow="sm">
                    {copyrightTotalLoading ? (
                      <Flex align="center" justify="center" minH="60px"><Spinner size="sm" /></Flex>
                    ) : (
                      <Flex align="center" justify="space-between" gap={4} wrap="wrap">
                        <HStack spacing={8} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="bold" color="blue.700">총 수익: <span style={{color:'#222'}}>{copyrightTotalSummary.total.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="teal.700">정산 완료 수익: <span style={{color:'#222'}}>{copyrightTotalSummary.completed.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="orange.600">미정산 수익: <span style={{color:'#222'}}>{copyrightTotalSummary.requested.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">국세: <span style={{color:'#222'}}>{copyrightTotalSummary.tax.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.600">지방세: <span style={{color:'#222'}}>{copyrightTotalSummary.localTax.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="purple.600">부가세: <span style={{color:'#222'}}>{copyrightTotalSummary.vat.toLocaleString()}원</span></Text>
                          <Text fontSize="sm" fontWeight="bold" color="green.700">최종 정산 금액: <span style={{color:'#222'}}>{copyrightTotalSummary.finalAmount.toLocaleString()}원</span></Text>
                        </HStack>
                        <Button colorScheme="green" size="sm" onClick={handleCopyrightExcelDownload}>
                          엑셀 다운로드
                        </Button>
                      </Flex>
                    )}
                  </Box>
                )}
                <SimpleGrid columns={17} spacing={2} fontWeight="bold" borderBottom="none" pb={2} fontSize="xs">
                  <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">회원</Text>
                  <Text textAlign="center">정산 계좌</Text>
                  <Text textAlign="center">정산 월</Text>
                  <Text textAlign="center">정산 타입</Text>
                  <Text textAlign="center">총 수익</Text>
                  <Text textAlign="center">국세</Text>
                  <Text textAlign="center">지방세</Text>
                  <Text textAlign="center">정산금</Text>
                  <Text textAlign="center">음원수익</Text>
                  <Text textAlign="center">영상수익(총)</Text>
                  <Text textAlign="center">영상수익(크리에이터)</Text>
                  <Text textAlign="center">영상수익(권리자+레이블리)</Text>
                  <Text textAlign="center">쇼핑수익(총)</Text>
                  <Text textAlign="center">쇼핑수익(크리에이터)</Text>
                  <Text textAlign="center">쇼핑수익(권리자+레이블리)</Text>
                  <Text textAlign="center">상태</Text>
                  <Text textAlign="center">처리</Text>
                </SimpleGrid>
                {loadingSettle ? (
                  <Flex justify="center" py={4}><Spinner /></Flex>
                ) : filteredCopyrightSettle.length > 0 ? (
                  <>
                    {filteredCopyrightSettle.map(item => renderSettleRow(item, "copyright"))}
                    <Flex justify="center" align="center" mt={4} gap={2}>
                      <Button size="sm" onClick={() => setCopyrightSettlePage(p => Math.max(1, p - 1))} isDisabled={copyrightSettlePage === 1}>이전</Button>
                      <Text fontWeight="bold" fontSize="sm">{copyrightSettlePage} / {copyrightSettleTotalPages}</Text>
                      <Button size="sm" onClick={() => setCopyrightSettlePage(p => Math.min(copyrightSettleTotalPages, p + 1))} isDisabled={copyrightSettlePage === copyrightSettleTotalPages}>다음</Button>
                    </Flex>
                  </>
                ) : (
                  <Text textAlign="center" py={4} color="gray.500">정산 신청 내역이 없습니다.</Text>
                )}
              </>
            )}
          </Box>
        )}
        {/* 정산 합계 영역 */}
        {mainTab === "summary" && (
          <Box>
            <Flex align="center" justify="space-between" mb={4} borderBottom="1px solid #e2e8f0" pb={2}>
              {/* 왼쪽: 사용자/영상 권리자 전환 */}
              <HStack spacing={0} mt={4}>
                <Button
                  borderRadius="2xl 0 0 2xl"
                  borderRightRadius={0}
                  fontWeight="bold"
                  fontSize="md"
                  px={8}
                  py={4}
                  bg={summaryTab === "user" ? "rgba(100,100,100,0.08)" : "transparent"}
                  color={summaryTab === "user" ? "teal.700" : "gray.500"}
                  border="1px solid #e2e8f0"
                  borderRight="none"
                  onClick={() => setSummaryTab("user")}
                >
                  사용자
                </Button>
                <Button
                  borderRadius="0 2xl 2xl 0"
                  borderLeftRadius={0}
                  fontWeight="bold"
                  fontSize="md"
                  px={8}
                  py={4}
                  bg={summaryTab === "copyright" ? "rgba(100,100,100,0.08)" : "transparent"}
                  color={summaryTab === "copyright" ? "teal.700" : "gray.500"}
                  border="1px solid #e2e8f0"
                  borderLeft="none"
                  onClick={() => setSummaryTab("copyright")}
                >
                  영상 권리자
                </Button>
              </HStack>
              {/* 오른쪽: 검색/필터 */}
              <Flex align="center" gap={4} ml="auto">
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>이메일</Text>
                  <HStack>
                    <input
                      type="text"
                      value={summaryEmailInput}
                      onChange={e => setSummaryEmailInput(e.target.value)}
                      placeholder="이메일 검색"
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', minWidth: 180, fontSize: '14px' }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          setSummaryEmail(summaryEmailInput);
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      colorScheme="teal"
                      onClick={() => setSummaryEmail(summaryEmailInput)}
                    >
                      검색
                    </Button>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>상태</Text>
                  <Select value={summaryStatus} onChange={e => setSummaryStatus(e.target.value)} w="120px">
                    <option value="">전체</option>
                    <option value="[User] Request">정산 대기</option>
                    <option value="[Admin] Completed">정산 완료</option>
                  </Select>
                </Box>
                <Button colorScheme="green" size="sm" onClick={handleSummaryExcelDownload} mt={6}>
                  엑셀 다운로드
                </Button>
              </Flex>
            </Flex>

            <SimpleGrid columns={9} spacing={2} fontWeight="bold" borderBottom="none" pb={2} fontSize="xs">
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">회원</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산 계좌</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">정산 타입</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">상태</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">총 수익</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">국세</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">지방세</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">부가세</Text>
              <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">최종 정산 금액</Text>
            </SimpleGrid>
            {summaryLoading ? (
              <Flex justify="center" py={4}><Spinner /></Flex>
            ) : summaryData.length > 0 ? (
              summaryData.map((row, idx) => {
                const payoutType = row.payout_type || '개인';
                const totalAmount = Number(row.total_payout_amount || 0);
                let tax = 0, localTax = 0, vat = 0, finalAmount = 0;
                if (payoutType === '개인') {
                  tax = Math.floor(totalAmount * 0.03);
                  localTax = Math.floor(totalAmount * 0.003);
                  vat = 0;
                  finalAmount = totalAmount - tax - localTax;
                } else {
                  tax = 0;
                  localTax = 0;
                  vat = Math.floor(totalAmount * 0.1);
                  finalAmount = totalAmount + vat;
                }
                const isPending = row.status === '[User] Request';
                return (
                  <SimpleGrid key={row.user + '-' + row.status + '-' + idx} columns={10} spacing={2} py={2} borderBottom="1px solid #f8f8f8" fontSize="sm" alignItems="center">
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">{row.user}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">{row.bank} {row.bank_account_number}<br/>{row.bank_account_holder}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">{payoutType}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">{row.status === '[Admin] Completed' ? '정산 완료' : row.status === '[User] Request' ? '정산 대기' : row.status}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">₩ {totalAmount.toLocaleString()}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">₩ {tax.toLocaleString()}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">₩ {localTax.toLocaleString()}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">₩ {vat.toLocaleString()}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">₩ {finalAmount.toLocaleString()}</Text>
                    <Text textAlign="center" display="flex" alignItems="center" justifyContent="center" h="100%">
                      {isPending && (
                        <Button size="xs" colorScheme="teal" onClick={() => handleSummaryCompleteAll(row.user)}>
                          전체 정산 완료
                        </Button>
                      )}
                    </Text>
                  </SimpleGrid>
                );
              })
            ) : (
              <Text textAlign="center" py={4} color="gray.500">정산 합계 내역이 없습니다.</Text>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
} 